import { RedoOutlined } from '@ant-design/icons';
import { Collapse, Tooltip } from 'antd';
import CollapseButton from 'components/CollapseButton';
import MapIt from 'components/MapIt';
import POIButton from 'components/POIButton';
import ScrollButtons from 'components/ScrollButtons';
import {
  setCurrInfoWindow,
  setCurrPOIs,
  setIsSideMenuCollapsed,
  setShowStaticMap,
} from 'features/vicinityMapSlice';
import gaFunction from 'lib/ga';
import { useVicinityMapQuery } from 'lib/hooks/queries';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import useWindowDimensions from 'lib/hooks/useWindowDimensions';
import type { IPOI } from 'lib/models/vicinityMap.interface';
import { getAllPOIs, getCategory, getGMCoordinates, resetGoogleMap } from 'lib/utils';
import { useState } from 'react';
import styled from 'styled-components';

const { Panel } = Collapse;

interface Props {
  directionsRenderer?: google.maps.DirectionsRenderer;
  map?: google.maps.Map;
}

const SideMenu = ({ directionsRenderer, map }: Props) => {
  const dispatch = useAppDispatch();
  const { showStaticMap } = useAppSelector((state) => ({
    showStaticMap: state.vicinityMap.showStaticMap,
  }));

  const vicinityMapData = useVicinityMapQuery().data!;

  const { isMobile } = useWindowDimensions();

  const [activeCategory, setActiveCategory] = useState<string[]>([]);
  const [scrollHeight, setScrollHeight] = useState(0);

  const getScrollHeight = () =>
    setTimeout(() => {
      setScrollHeight(
        document.querySelector('.ant-collapse')!.scrollHeight -
          document.querySelector('.ant-collapse')!.clientHeight
      );
    }, 301);

  const handleCategoryClick = (e: string | string[] | undefined) => {
    // if map and directionsRenderer have defined values, handleCategoryClick function codeblock runs
    if (map && directionsRenderer) {
      // update scroll height state, set timeout for css animation
      getScrollHeight();

      let newMarkers: IPOI[] = [];

      // if they clicked on an disabled category button that is NOT the community name
      if (typeof e === 'string') {
        // GA
        gaFunction.vmOpenCategoryMenu(e);
        setActiveCategory([e]);
        // create a new array of POIs that match the category and extend the bounds by each POI
        const bounds = new google.maps.LatLngBounds();
        // extend the bounds by the project location
        bounds.extend(getGMCoordinates(vicinityMapData.main.projectLocationCoordinates));
        // extend the bounds by each POI in the selected category
        vicinityMapData.categories.forEach((category) => {
          if (category.title === e) {
            category.pois.forEach((poi) => {
              if (poi.coordinates) {
                bounds.extend(getGMCoordinates(poi.coordinates));
                newMarkers.push(poi);
              }
            });
          }
        });
        // fit the bounds to the map
        map.fitBounds(bounds);
        map.setZoom(map.getZoom()! - 1);
        map.panTo(bounds.getCenter());
      } else {
        setActiveCategory([]);
        // if they clicked on the community name or an already active category, reset the POI markers to default (all) and fit the map to show all POIs
        newMarkers = getAllPOIs(vicinityMapData.categories);
        resetGoogleMap({ map, data: vicinityMapData });
      }
      // close any open info window
      dispatch(setCurrInfoWindow());
      // update curr POIs
      dispatch(setCurrPOIs(newMarkers));

      // clear directions
      directionsRenderer.setMap(null);
    }
  };

  const resetMapItInput = () => {
    const mapItInput = document.getElementById('map-it-input') as HTMLInputElement;
    mapItInput.value = '';
  };

  const resetMap = () => {
    if (map && directionsRenderer) {
      // reset curr POIs
      const allMarkers: IPOI[] = getAllPOIs(vicinityMapData.categories);
      dispatch(setCurrPOIs(allMarkers));

      // close category dropdowns
      setActiveCategory([]);

      // update scroll height state, set timeout for css animation
      getScrollHeight();

      // close any open info window
      dispatch(setCurrInfoWindow());

      if (isMobile) {
        dispatch(setIsSideMenuCollapsed(true));
      }

      // clear directions
      directionsRenderer.setMap(null);

      resetGoogleMap({ map, data: vicinityMapData });

      // reset mapIt input value to placeholder
      resetMapItInput();
    }
  };

  return (
    <StyledSideMenuContainer
      id="side-menu-container"
      className="cy-side-menu"
      data-cy="side-menu"
      onClick={() => {
        showStaticMap && dispatch(setShowStaticMap(false));
      }}
    >
      {/* if side menu is collapsible, show collapse button */}
      {isMobile || !!vicinityMapData.main.sideMenuCollapsible ? <CollapseButton /> : null}
      <div>
        <Header>
          <SideMenuTitle id="map-filter-text">FILTER</SideMenuTitle>
          <Tooltip title="Reset Filters">
            <ResetButton
              id="reset-button"
              className="cy-reset-button"
              data-cy="reset-button"
              onClick={() => resetMap()}
            >
              <RedoOutlined />
            </ResetButton>
          </Tooltip>
        </Header>
        <div id="buttons-container">
          <ScrollButtons scrollHeight={scrollHeight} setScrollHeight={setScrollHeight}>
            <Collapse accordion onChange={handleCategoryClick} activeKey={activeCategory} ghost>
              {/* category and poi buttons */}
              {vicinityMapData.categories.map((category) => (
                <Panel
                  id={category.title.replaceAll(' ', '-') + '-button'}
                  className="category-button cy-category-button"
                  key={category.title}
                  showArrow={false}
                  header={
                    <>
                      {category.markerUrl ? (
                        <CategoryIcon
                          src={`${vicinityMapData.systemProperties.BASE_ASSET_URL}${
                            getCategory(category.sysId, vicinityMapData)?.markerUrl?.replace(
                              '.png',
                              ''
                            ) + '_Icon.svg'
                          }`}
                          alt={category.title}
                        />
                      ) : (
                        <div />
                      )}
                      <span
                        id={category.title.replaceAll(' ', '-') + '-text'}
                        className="category-text"
                      >
                        {category.title}
                      </span>
                    </>
                  }
                >
                  <POIButtonsContainer
                    id={category.title.replaceAll(' ', '-') + '-poi-buttons-container'}
                  >
                    {category.pois.map(
                      (poi) =>
                        poi.coordinates && (
                          <POIButton
                            key={poi.sysId}
                            POI={poi}
                            directionsRenderer={directionsRenderer}
                          />
                        )
                    )}
                  </POIButtonsContainer>
                </Panel>
              ))}
            </Collapse>
          </ScrollButtons>
        </div>
      </div>
      {/* if hide map it is off, show map it */}
      {!vicinityMapData.main.hideMapIt && (
        <MapIt map={map} directionsRenderer={directionsRenderer} />
      )}
    </StyledSideMenuContainer>
  );
};

export default SideMenu;

export const StyledSideMenuContainer = styled.div`
  /* if the side menu is on the right, set left: auto, otherwise
  if side menu is collapsed, set left: -{sideMenuWidth}%, otherwise set left: 0 */
  left: ${({ theme }) =>
    theme.layout.sideMenuPosition === 'right'
      ? 'auto'
      : theme.layout.isSideMenuCollapsed
      ? `calc(0px - ${theme.layout.sideMenuWidth} - 1px)`
      : '0'};
  /* if the side menu is on the left, set right: auto, otherwise
  if side menu is collapsed, set right: -{sideMenuWidth}%, otherwise set right: 0 */
  right: ${({ theme }) =>
    theme.layout.sideMenuPosition === 'left'
      ? 'auto'
      : theme.layout.isSideMenuCollapsed
      ? `calc(0px - ${theme.layout.sideMenuWidth} - 1px)`
      : '0'};
  background-color: ${({ theme }) => theme.colors.sideMenuBackgroundColor};
  position: absolute;
  top: 0;
  height: 100%;
  width: ${({ theme }) => theme.layout.sideMenuWidth};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.5s ease-in-out;
  padding: 1rem;
  z-index: 1000;
  box-shadow: 0px 4px 8px rgb(6 19 31 / 12%);

  .ant-collapse {
    position: relative;
    border: none;
    background-color: inherit;
    display: flex;
    flex-direction: column;
    gap: 1vh;
    max-height: 65vh;
    width: 80%;
    margin: auto;
    .ant-collapse-header {
      display: grid;
      grid-template-columns: 10% 1fr 10%;
      place-items: center;
      padding: 2% 6%;
      color: ${({ theme }) => theme.colors.textColorCategory};
      background-color: ${({ theme }) => theme.colors.buttonColorCategory};
      border-radius: 8px !important;
      font-size: clamp(14px, 1.2vw, 16px);
      text-align: center;

      &:hover {
        color: ${({ theme }) => theme.colors.primaryTextColor};
        background-color: ${({ theme }) => theme.colors.primaryColor};
      }
      .anticon {
        display: none;
      }
    }

    .ant-collapse-content-box {
      padding: 4% 0 4% 10%;
    }

    .ant-collapse-content {
      width: 100%;
      border: none;
      background-color: inherit !important;
    }

    .ant-collapse-item-active > .ant-collapse-header {
      color: ${({ theme }) => theme.colors.primaryTextColor};
      background-color: ${({ theme }) => theme.colors.primaryColor};
    }
  }
`;

export const POIButtonsContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  list-style: none;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  border-bottom: 1px solid #c3c2c1;
`;

export const ResetButton = styled.button`
  color: ${({ theme }) => theme.colors.textColorPoi};
  background-color: ${({ theme }) => theme.colors.buttonColorPoi};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  border: none;
  font-size: max(0.8vw, 8px);
  padding: 4px 6px;
  cursor: pointer;
  @media screen and (min-width: 768px) and (max-height: 768px) and (max-width: 1000px) {
    font-size: max(1.5vw, 8px);
  }
`;

export const SideMenuTitle = styled.h4`
  color: ${({ theme }) => theme.colors.primaryColor};
  font-size: max(16px, 1.1vw);
  font-weight: 500;
  margin: 0;
`;

export const CategoryIcon = styled.img`
  width: 100%;
  height: 100%;
  .st0 {
    opacity: 1;
  }
  .st1 {
    opacity: 1;
    fill: ${({ theme }) => theme.colors.textColorCategory};
  }
`;
